/**
 * Designing — shared vocabulary for every screen of the module.
 *
 * Designing is the report-preparation-only operation: a site is created, its
 * data is collected, a report maker prepares the report, a validator finalizes
 * it and it is sent to the client. No field work, no engineer, no TA review.
 *
 * Values here mirror server/src/models/design_site.model.ts. Permissions are
 * one-per-task under the `DesignSite` resource (server/src/helpers/
 * design_site.helper.ts) — there is no umbrella permission and no role-id check
 * anywhere in this module, so every button asks for exactly the permission its
 * endpoint enforces.
 */

export const DESIGN_RESOURCE = "DesignSite";

export const DESIGN_ACTIONS = {
  VIEW_ALL: "view_all",
  VIEW_MY: "view_my",
  VIEW: "view",
  CREATE: "create",
  EDIT: "edit",
  UPLOAD: "upload",
  EXPORT: "export",
  DATA_UPLOAD: "data_upload",
  ASSIGN_REPORT_MAKER: "assign_report_maker",
  REPORT_REJECT: "report_reject",
  REPORT_UPLOAD: "report_upload",
  ASSIGN_VALIDATOR: "assign_validator",
  REPORT_FINALIZE: "report_finalize",
  VALIDATOR_REJECT: "validator_reject",
  SEND_TO_CLIENT: "send_to_client",
  REOPEN: "reopen",
  CANCEL: "cancel",
  BULK_CANCEL: "bulk_cancel",
  HOLD: "hold",
  RESUME: "resume",
  FILES_ADD: "files_add",
  FILES_DELETE: "files_delete",
  HISTORY: "history",
  /** Retired - replaced by VIEW_BILLING / VIEW_VENDOR. */
  VIEW_COMMERCIALS: "view_commercials",
  VIEW_BILLING: "view_billing",
  VIEW_VENDOR: "view_vendor",
  BILLING_UPDATE: "billing_update",
  VENDOR_UPDATE: "vendor_update",
} as const;

export type Option = { value: string; label: string };

// ── Each report maker's own report ───────────────────────────────────────────
// Mirrors DesignReportMakerStatus (server/src/models/design_site_report_maker.model.ts).
// The site leaves Report Maker Assigned only when every report maker has
// submitted; it can only be finalized when the validator has accepted every one.
export const REPORT_MAKER_STATUSES: Option[] = [
  { value: "pending", label: "Report pending" },
  { value: "submitted", label: "Submitted" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Sent back" },
];

export const reportMakerStatusLabel = (value?: string | null): string =>
  REPORT_MAKER_STATUSES.find((s) => s.value === value)?.label || "Report pending";

export const reportMakerStatusBadgeClass = (value?: string | null): string => {
  switch (value) {
    case "submitted":
      return "bg-blue-50 text-blue-800 border border-blue-200";
    case "accepted":
      return "bg-green-50 text-green-800 border border-green-200";
    case "rejected":
      return "bg-red-50 text-red-700 border border-red-200";
    default:
      return "bg-gray-100 text-gray-700 border border-gray-200";
  }
};

export interface ReportProgress {
  total: number;
  pending: number;
  submitted: number;
  accepted: number;
  rejected: number;
}

/** Progress of a team computed on the client (lists carry rows, not totals). */
export const teamProgress = (team: Array<{ report_status?: string | null }> = []): ReportProgress => {
  const count = (status: string) => team.filter((m) => (m.report_status || "pending") === status).length;
  return { total: team.length, pending: count("pending"), submitted: count("submitted"), accepted: count("accepted"), rejected: count("rejected") };
};

// ── Workflow stages, in order ────────────────────────────────────────────────
export const DESIGN_STAGES: Option[] = [
  { value: "site_created", label: "Site Created" },
  { value: "site_data_uploaded", label: "Site Data Uploaded" },
  { value: "report_maker_assigned", label: "Report Maker Assigned" },
  { value: "report_completed", label: "Report Completed" },
  { value: "validator_assigned", label: "Validator Assigned" },
  { value: "report_finalized", label: "Report Finalized" },
  { value: "report_sent_to_client", label: "Report Sent to Client" },
];

export const stageLabel = (value?: string | null): string =>
  DESIGN_STAGES.find((s) => s.value === value)?.label || (value || "-").replace(/_/g, " ");

export const stageBadgeClass = (value?: string | null): string => {
  switch (value) {
    case "site_created":
      return "bg-gray-100 text-gray-700";
    case "site_data_uploaded":
      return "bg-sky-100 text-sky-800";
    case "report_maker_assigned":
      return "bg-indigo-100 text-indigo-800";
    case "report_completed":
      return "bg-blue-100 text-blue-800";
    case "validator_assigned":
      return "bg-purple-100 text-purple-800";
    case "report_finalized":
      return "bg-teal-100 text-teal-800";
    case "report_sent_to_client":
      return "bg-green-100 text-green-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

// ── Work completion status ───────────────────────────────────────────────────
export const DESIGN_WORK_STATUSES: Option[] = [
  { value: "in_progress", label: "In Progress" },
  { value: "hold", label: "Hold" },
  { value: "cancelled", label: "Cancelled" },
  { value: "completed", label: "Completed" },
  { value: "reopened", label: "Reopened" },
];

export const workStatusLabel = (value?: string | null): string =>
  DESIGN_WORK_STATUSES.find((s) => s.value === value)?.label || (value || "-").replace(/_/g, " ");

export const workStatusBadgeClass = (value?: string | null): string => {
  switch (value) {
    case "in_progress":
      return "bg-blue-100 text-blue-800";
    case "hold":
      return "bg-amber-100 text-amber-800";
    case "cancelled":
      return "bg-red-100 text-red-800";
    case "completed":
      return "bg-green-100 text-green-800";
    case "reopened":
      return "bg-orange-100 text-orange-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

// ── Site-created fields ──────────────────────────────────────────────────────
export const DESIGN_REPORT_TYPES: Option[] = [
  { value: "new", label: "New" },
  { value: "revision", label: "Revision" },
];

export const reportTypeLabel = (value?: string | null): string =>
  DESIGN_REPORT_TYPES.find((s) => s.value === value)?.label || value || "-";

export const DESIGN_PRIORITIES: Option[] = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

export const priorityLabel = (value?: string | null): string =>
  DESIGN_PRIORITIES.find((s) => s.value === value)?.label || value || "-";

export const priorityBadgeClass = (value?: string | null): string => {
  switch (value) {
    case "critical":
      return "bg-red-100 text-red-800";
    case "high":
      return "bg-orange-100 text-orange-800";
    case "medium":
      return "bg-yellow-100 text-yellow-800";
    case "low":
      return "bg-gray-100 text-gray-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

// How site data / the finished report was handed over when it was not (only)
// uploaded on the portal.
export const DESIGN_SHARE_MODES: Option[] = [
  { value: "portal", label: "Uploaded on portal" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "email", label: "Email" },
  { value: "drive", label: "Drive" },
  { value: "drive_link", label: "Drive link" },
  { value: "other", label: "Other" },
];

export const shareModeLabel = (value?: string | null): string =>
  DESIGN_SHARE_MODES.find((s) => s.value === value)?.label || value || "-";

// ── Commercials (only for holders of DesignSite.view_commercials) ────────────
export const DESIGN_BILLING_STATUSES: Option[] = [
  { value: "pending", label: "Pending" },
  { value: "wcc_raised", label: "WCC Raised" },
  { value: "wcc_approved", label: "WCC Approved" },
  { value: "invoice_raised", label: "Invoice Raised" },
  { value: "invoice_submitted", label: "Invoice Submitted" },
  { value: "paid", label: "Paid" },
  { value: "not_billable", label: "Not Billable" },
];

export const billingStatusLabel = (value?: string | null): string =>
  DESIGN_BILLING_STATUSES.find((s) => s.value === value)?.label || value || "-";

export const DESIGN_VENDOR_STATUSES: Option[] = [
  { value: "not_applicable", label: "Not Applicable (In-house)" },
  { value: "po_not_required", label: "PO Not Required" },
  { value: "po_not_issued", label: "PO Not Issued" },
  { value: "po_generated", label: "PO Generated" },
  { value: "invoice_submitted", label: "Invoice Submitted" },
  { value: "invoice_booked", label: "Invoice Booked" },
  { value: "invoice_rejected", label: "Invoice Rejected" },
];

export const vendorStatusLabel = (value?: string | null): string =>
  DESIGN_VENDOR_STATUSES.find((s) => s.value === value)?.label || value || "-";

// ── Files ────────────────────────────────────────────────────────────────────
// upload_stage values of design_site_files, and the S3 folder each goes to.
export const DESIGN_FILE_STAGES: Array<Option & { folder: string }> = [
  { value: "site_data_uploaded", label: "Site Data", folder: "designing/site-data" },
  { value: "report_completed", label: "Report", folder: "designing/reports" },
  { value: "report_finalized", label: "Validation Documents", folder: "designing/validator-files" },
];

export const fileStageFolder = (stage: string): string =>
  DESIGN_FILE_STAGES.find((s) => s.value === stage)?.folder || "designing/site-data";

export const DESIGN_FILE_ACCEPT = "image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.dwg";

// ── Formatting ───────────────────────────────────────────────────────────────
export const personName = (u?: { first_name?: string | null; last_name?: string | null; username?: string | null } | null): string =>
  u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username || "-" : "-";

// A date-only value (required date, PO date) — never run through a time zone.
export const formatDateOnly = (value?: string | null): string => {
  if (!value) return "-";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!match) return String(value);
  const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

// A moment (stage dates, last updated).
export const formatDateTime = (value?: string | null): string => {
  if (!value) return "-";
  const d = new Date(value);
  return isNaN(d.getTime())
    ? "-"
    : d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export const formatAmount = (value?: number | string | null): string => {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(value);
};

// The stage dates of a site, in workflow order — the "All Stage Date" columns.
export const DESIGN_STAGE_DATE_COLUMNS: Array<{ key: string; label: string; filter: string }> = [
  { key: "created_at", label: "Site Created Date", filter: "created" },
  { key: "data_uploaded_at", label: "Site Data Uploaded Date", filter: "data_uploaded" },
  { key: "report_maker_assigned_at", label: "Report Maker Assigned Date", filter: "report_maker_assigned" },
  { key: "report_completed_at", label: "Report Completed Date", filter: "report_completed" },
  { key: "validator_assigned_at", label: "Validator Assigned Date", filter: "validator_assigned" },
  { key: "report_finalized_at", label: "Report Finalized Date", filter: "report_finalized" },
  { key: "sent_to_client_at", label: "Report Sent to Client Date", filter: "sent_to_client" },
];
